import * as XLSX from "xlsx";

export const REPORT_MODES = {
  DESPATCH: "completed-despatch-scans",
  WAREHOUSE: "warehouse-removal-events",
  NAV: "nav-open-stock-mismatch",
};

export const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  serialNumber: "",
  productId: "",
  productCode: "",
  special: "",
  location: "",
  despatchReportNumber: "",
  classification: "",
};

const MODE_EXPORT_DETAILS = {
  [REPORT_MODES.DESPATCH]: {
    label: "Completed despatch report scans",
    filename: "completed-despatch-scans",
  },
  [REPORT_MODES.WAREHOUSE]: {
    label: "Warehouse removal events",
    filename: "warehouse-removal-events",
  },
  [REPORT_MODES.NAV]: {
    label: "NAV open-stock mismatch",
    filename: "nav-open-stock-mismatch",
  },
};

export function asString(value) {
  return value == null ? "" : String(value).trim();
}

export function normaliseKeyPart(value) {
  return asString(value).toUpperCase();
}

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === "function") {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime())
      ? converted
      : null;
  }
  if (Number.isFinite(value.seconds)) {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  const converted = new Date(value);
  return Number.isNaN(converted.getTime()) ? null : converted;
}

export function formatDateTime(value) {
  const date = toDate(value);
  return date ? date.toLocaleString("en-GB") : "";
}

export function stripSO(documentNumber) {
  return asString(documentNumber).replace(/^SO/i, "").trim();
}

export function reconstructBarcode(serialNumber, special, productId) {
  const serial = asString(serialNumber);
  const specialCode = asString(special);
  const product = asString(productId);

  if (
    !/^\d{6}$/.test(serial) ||
    !/^\d{2}$/.test(specialCode) ||
    !/^\d{5}$/.test(product)
  ) {
    return "";
  }

  return `${serial}${specialCode}${product}`;
}

export function buildProductLookups(productDocuments) {
  const byId = new Map();
  const idsByCode = new Map();

  for (const product of productDocuments || []) {
    const id = asString(product.id);
    if (!id) continue;

    const productCode = asString(product.productCode);
    const normalized = {
      ...product,
      id,
      productCode,
      productDescription: asString(product.productDescription),
    };
    byId.set(id, normalized);

    const codeKey = normaliseKeyPart(productCode);
    if (codeKey) {
      const existing = idsByCode.get(codeKey) || [];
      existing.push(id);
      idsByCode.set(codeKey, existing);
    }
  }

  return { byId, idsByCode };
}

export function buildLocationLookup(locationDocuments) {
  const bySuffix = new Map();

  for (const location of locationDocuments || []) {
    const barcode = asString(location.barcode);
    if (!barcode) continue;
    bySuffix.set(barcode.slice(-5), asString(location.id) || barcode);
  }

  return bySuffix;
}

export function resolveLocation(locationId, locationBySuffix) {
  const raw = asString(locationId);
  if (!raw || !locationBySuffix) return raw;
  return locationBySuffix.get(raw.slice(-5)) || raw;
}

export function classifyArchiveRecord(record) {
  const reason = asString(record.Reason);
  const method = asString(record.BookedOutMethod);
  const manualReason = asString(record.ManualBookOutReason);
  const combined = `${reason} ${method} ${manualReason}`.toLowerCase();

  if (
    combined.includes("pre-allocated update backup") ||
    combined.includes("pre-sync version archived before update") ||
    combined.includes("stocktake backup")
  ) {
    return "Stocktake backup";
  }

  if (record.ManualBookOut === true || method === "manual_warehouse_book_out") {
    return "Manual removal";
  }

  if (combined.includes("dead stock") || combined.includes("dead-stock")) {
    return "Dead-stock removal";
  }

  if (
    method === "standard_warehouse_book_out" ||
    method === "warehouse_booking_out"
  ) {
    return "Standard booking-out";
  }

  return "Unclassified removal";
}

function productFor(productId, productById) {
  return productById?.get(asString(productId)) || null;
}

export function buildCompletedDespatchRows(
  reportDocuments,
  detailDocuments,
  productById
) {
  const completedById = new Map(
    (reportDocuments || [])
      .filter((report) => report.completed === true)
      .map((report) => [asString(report.id), report])
  );

  const rows = [];

  for (const detail of detailDocuments || []) {
    const detailId = asString(detail.id);
    const report = completedById.get(detailId);
    if (!report) continue;

    const items = Array.isArray(detail.items) ? detail.items : [];
    items.forEach((item, index) => {
      const barcode = asString(item.barcode);
      const productId = asString(item.productId);
      const product = productFor(productId, productById);
      const embeddedCode = asString(item.productCode);
      const productCode =
        embeddedCode && !/^Unknown/i.test(embeddedCode)
          ? embeddedCode
          : asString(product?.productCode) || embeddedCode;
      const rawSpecial =
        /^\d{13}$/.test(barcode) ? barcode.slice(6, 8) : "";
      const itemId = asString(item.id) || `item-${index + 1}`;
      const reportNumber = asString(report.reportNumber);

      rows.push({
        rowId: `despatch:${detailId}:${itemId}:${index}`,
        mode: REPORT_MODES.DESPATCH,
        fullBarcode: barcode,
        serialNumber: asString(item.serialNumber),
        productId,
        productCode,
        productDescription: asString(product?.productDescription),
        special: asString(item.special),
        specialRaw: rawSpecial,
        location: "",
        primaryDate: toDate(item.scannedAt),
        itemScannedAt: toDate(item.scannedAt),
        reportCreatedAt: toDate(report.createdAt),
        reportNumber,
        reportHeaderId: asString(report.id),
        reportDetailId: detailId,
        itemId,
        classification: "Completed despatch scan",
        status: "Completed despatch scan",
        sourceDocumentIds: [asString(report.id), detailId].filter(Boolean),
        reason: `Barcode was scanned onto completed despatch report ${
          reportNumber || "(number unavailable)"
        }`,
        warnings: [],
      });
    });
  }

  const totals = new Map();
  for (const row of rows) {
    const key = normaliseKeyPart(row.fullBarcode);
    if (key) totals.set(key, (totals.get(key) || 0) + 1);
  }

  const occurrence = new Map();
  return rows.map((row) => {
    const key = normaliseKeyPart(row.fullBarcode);
    const duplicateCount = key ? totals.get(key) || 1 : 1;
    const occurrenceNumber = key ? (occurrence.get(key) || 0) + 1 : 1;
    if (key) occurrence.set(key, occurrenceNumber);

    return {
      ...row,
      duplicateCount,
      occurrenceNumber,
      isDuplicate: duplicateCount > 1,
    };
  });
}

export function buildWarehouseRemovalRows(
  archiveDocuments,
  productById,
  locationBySuffix
) {
  const rows = (archiveDocuments || []).map((record) => {
    const archiveId = asString(record.id);
    const serialNumber = asString(record.SerialNumber);
    const productId = asString(record.ProductId);
    const special = asString(record.Special);
    const product = productFor(productId, productById);
    const classification = classifyArchiveRecord(record);
    const removalReason =
      asString(record.ManualBookOutReason) || asString(record.Reason);
    const removalMethod = asString(record.BookedOutMethod);
    const notes = asString(record.ManualBookOutNotes);
    const actor =
      asString(record.ManualBookOutByName) ||
      asString(record.ManualBookOutByEmail);
    const originalSourceId = asString(
      record.ManualBookOutSourceDocumentId || record.SourceDocumentId
    );

    let reason;
    if (classification === "Stocktake backup") {
      reason =
        "Archive record was created as a stocktake backup and is excluded from default removal results.";
    } else if (classification === "Manual removal") {
      reason = `Cylinder was removed from live warehouse data using the manual removal workflow${
        removalReason ? `: ${removalReason}` : "."
      }`;
    } else if (classification === "Dead-stock removal") {
      reason =
        "Cylinder was removed from live warehouse data by an explicitly identified dead-stock workflow.";
    } else if (classification === "Standard booking-out") {
      reason =
        "Cylinder was removed from live warehouse data by an explicitly identified standard booking-out workflow.";
    } else {
      reason =
        "Record exists in warehouse_archive, but the source workflow is not recorded; physical departure is not proven.";
    }

    const warnings = [];
    if (!product) warnings.push("Product mapping unavailable");
    if (!record.BookedOutTime) warnings.push("Booked-out time unavailable");

    return {
      rowId: `warehouse:${archiveId}`,
      mode: REPORT_MODES.WAREHOUSE,
      fullBarcode: reconstructBarcode(serialNumber, special, productId),
      serialNumber,
      productId,
      productCode: asString(product?.productCode),
      productDescription: asString(product?.productDescription),
      special,
      specialRaw: special,
      location: resolveLocation(record.LocationId, locationBySuffix),
      rawLocation: asString(record.LocationId),
      primaryDate: toDate(record.BookedOutTime),
      bookedOutAt: toDate(record.BookedOutTime),
      removalMethod,
      removalReason,
      notes,
      actor,
      classification,
      status: classification,
      archiveDocumentId: archiveId,
      originalSourceId,
      sourceDocumentIds: [archiveId, originalSourceId].filter(Boolean),
      reason,
      warnings,
    };
  });

  const identityTotals = new Map();
  for (const row of rows) {
    const identityKey =
      normaliseKeyPart(row.fullBarcode) ||
      [
        normaliseKeyPart(row.serialNumber),
        normaliseKeyPart(row.productId),
        normaliseKeyPart(row.special),
      ]
        .filter(Boolean)
        .join("__");
    if (identityKey) {
      identityTotals.set(
        identityKey,
        (identityTotals.get(identityKey) || 0) + 1
      );
    }
  }

  const identityOccurrence = new Map();
  return rows.map((row) => {
    const identityKey =
      normaliseKeyPart(row.fullBarcode) ||
      [
        normaliseKeyPart(row.serialNumber),
        normaliseKeyPart(row.productId),
        normaliseKeyPart(row.special),
      ]
        .filter(Boolean)
        .join("__");
    const duplicateCount = identityKey
      ? identityTotals.get(identityKey) || 1
      : 1;
    const occurrenceNumber = identityKey
      ? (identityOccurrence.get(identityKey) || 0) + 1
      : 1;
    if (identityKey) {
      identityOccurrence.set(identityKey, occurrenceNumber);
    }

    return {
      ...row,
      duplicateCount,
      occurrenceNumber,
      isDuplicate: duplicateCount > 1,
    };
  });
}

function warehouseMatchKey(productId, serialNumber) {
  const product = normaliseKeyPart(productId);
  const serial = normaliseKeyPart(serialNumber);
  return product && serial ? `${product}__${serial}` : "";
}

function pushToMapArray(map, key, value) {
  const existing = map.get(key) || [];
  existing.push(value);
  map.set(key, existing);
}

export function buildNavMismatchRows({
  warehouseDocuments,
  navDocuments,
  productById,
  productIdsByCode,
  locationBySuffix,
  navComplete,
}) {
  const navByKey = new Map();
  const ambiguousProductIds = new Set();
  const unusableIdentityProductIds = new Set();
  const diagnostics = {
    invalidNavRows: 0,
    missingProductMappings: 0,
    ambiguousProductMappings: 0,
  };

  for (const navRecord of navDocuments || []) {
    const productCode = asString(navRecord.SerialNumber);
    const serialNumber = stripSO(navRecord.DocumentNo);
    const productIds =
      productIdsByCode?.get(normaliseKeyPart(productCode)) || [];

    if (productIds.length === 0) {
      diagnostics.missingProductMappings += 1;
      continue;
    }

    if (productIds.length > 1) {
      diagnostics.ambiguousProductMappings += 1;
      productIds.forEach((productId) => ambiguousProductIds.add(productId));
      continue;
    }

    const productId = productIds[0];
    const key = warehouseMatchKey(productId, serialNumber);
    if (!key) {
      diagnostics.invalidNavRows += 1;
      unusableIdentityProductIds.add(productId);
      continue;
    }

    pushToMapArray(navByKey, key, navRecord);
  }

  const warehouseStock = (warehouseDocuments || []).filter(
    (record) => record.Stock === true && record.Dispatch === false
  );
  const warehouseByKey = new Map();

  for (const warehouseRecord of warehouseStock) {
    const key = warehouseMatchKey(
      warehouseRecord.ProductId,
      warehouseRecord.SerialNumber
    );
    if (key) pushToMapArray(warehouseByKey, key, warehouseRecord);
  }

  const rows = warehouseStock.map((record) => {
    const warehouseId = asString(record.id);
    const serialNumber = asString(record.SerialNumber);
    const productId = asString(record.ProductId);
    const special = asString(record.Special);
    const product = productFor(productId, productById);
    const key = warehouseMatchKey(productId, serialNumber);
    const warehouseCandidates = key ? warehouseByKey.get(key) || [] : [];
    const navCandidates = key ? navByKey.get(key) || [] : [];
    const warehouseOccurrenceCount = warehouseCandidates.length || 1;
    const navOccurrenceCount = navCandidates.length;

    let status;
    let reason;

    if (!key) {
      status = "Invalid identity";
      reason =
        "The live warehouse record does not contain both ProductId and SerialNumber, so it cannot be compared.";
    } else if (!product) {
      status = "Missing product mapping";
      reason =
        "The warehouse ProductId has no cyl_info record, so it cannot be mapped to the NAV product code.";
    } else if (ambiguousProductIds.has(productId)) {
      status = "Ambiguous product mapping";
      reason =
        "The NAV product code maps to more than one ProductId, so no deterministic NAV comparison is possible.";
    } else if (warehouseOccurrenceCount > 1) {
      status = "Ambiguous duplicate match";
      reason = `${warehouseOccurrenceCount} live warehouse records share this ProductId and SerialNumber; NAV does not provide Special, so individual occurrences cannot be paired safely.`;
    } else if (navOccurrenceCount > 0) {
      status = "NAV match found";
      reason =
        "At least one matching occurrence exists in the loaded NAV open-stock feed.";
    } else if (
      !navComplete ||
      unusableIdentityProductIds.has(productId)
    ) {
      status = "NAV comparison incomplete";
      reason =
        "A definitive absence cannot be reported because the NAV dataset or relevant NAV identity data is incomplete.";
    } else {
      status = "No NAV open-stock match";
      reason =
        "No matching ProductId and SerialNumber occurrence exists in the complete loaded NAV open-stock collection.";
    }

    const matchedNavIds = navCandidates.map((navRecord) =>
      asString(navRecord.id)
    );

    return {
      rowId: `nav:${warehouseId}`,
      mode: REPORT_MODES.NAV,
      fullBarcode: reconstructBarcode(serialNumber, special, productId),
      serialNumber,
      productId,
      productCode: asString(product?.productCode),
      productDescription: asString(product?.productDescription),
      special,
      specialRaw: special,
      location: resolveLocation(record.LocationId, locationBySuffix),
      rawLocation: asString(record.LocationId),
      primaryDate: null,
      warehouseDocumentId: warehouseId,
      navDocumentIds: matchedNavIds,
      warehouseOccurrenceCount,
      navOccurrenceCount,
      duplicateCount: warehouseOccurrenceCount,
      occurrenceNumber:
        warehouseCandidates.findIndex(
          (candidate) => asString(candidate.id) === warehouseId
        ) + 1,
      isDuplicate: warehouseOccurrenceCount > 1,
      classification: status,
      status,
      sourceDocumentIds: [warehouseId, ...matchedNavIds].filter(Boolean),
      reason,
      warnings: [],
    };
  });

  return { rows, diagnostics };
}

function includesText(value, search) {
  return normaliseKeyPart(value).includes(normaliseKeyPart(search));
}

function startOfDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function endOfDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function applyReportFilters(rows, filters, mode) {
  const dateFrom = startOfDate(filters.dateFrom);
  const dateTo = endOfDate(filters.dateTo);

  return (rows || []).filter((row) => {
    if (
      mode === REPORT_MODES.WAREHOUSE &&
      !filters.classification &&
      row.classification === "Stocktake backup"
    ) {
      return false;
    }

    if (dateFrom || dateTo) {
      const rowDate = toDate(row.primaryDate);
      if (!rowDate) return false;
      if (dateFrom && rowDate < dateFrom) return false;
      if (dateTo && rowDate > dateTo) return false;
    }

    if (
      filters.serialNumber &&
      !includesText(row.serialNumber, filters.serialNumber)
    ) {
      return false;
    }
    if (filters.productId && !includesText(row.productId, filters.productId)) {
      return false;
    }
    if (
      filters.productCode &&
      !includesText(row.productCode, filters.productCode)
    ) {
      return false;
    }
    if (
      filters.special &&
      !includesText(
        `${row.special || ""} ${row.specialRaw || ""}`,
        filters.special
      )
    ) {
      return false;
    }
    if (filters.location && !includesText(row.location, filters.location)) {
      return false;
    }
    if (
      filters.despatchReportNumber &&
      !includesText(row.reportNumber, filters.despatchReportNumber)
    ) {
      return false;
    }
    if (
      filters.classification &&
      filters.classification !== "__all__" &&
      row.classification !== filters.classification &&
      row.status !== filters.classification
    ) {
      return false;
    }

    return true;
  });
}

function exportColumns(mode) {
  const identity = [
    ["Full Barcode", "fullBarcode", true],
    ["Serial Number", "serialNumber", true],
    ["Product ID", "productId", true],
    ["Product Code", "productCode", true],
    ["Product Description", "productDescription", false],
    ["Special / Type", "special", true],
  ];

  if (mode === REPORT_MODES.DESPATCH) {
    return [
      ...identity,
      ["Despatch Report Number", "reportNumber", true],
      ["Item Scan Time", "itemScannedAt", false, true],
      ["Report Creation Time", "reportCreatedAt", false, true],
      ["Barcode Occurrence Count", "duplicateCount", false],
      ["Occurrence Number", "occurrenceNumber", false],
      ["Report Header ID", "reportHeaderId", true],
      ["Report Detail ID", "reportDetailId", true],
      ["Item UUID", "itemId", true],
      ["Reason", "reason", false],
    ];
  }

  if (mode === REPORT_MODES.WAREHOUSE) {
    return [
      ...identity,
      ["Last Known Location", "location", true],
      ["Booked-out Time", "bookedOutAt", false, true],
      ["Classification", "classification", false],
      ["Removal Method", "removalMethod", false],
      ["Removal Reason", "removalReason", false],
      ["Notes", "notes", false],
      ["Actor / User", "actor", false],
      ["Barcode Occurrence Count", "duplicateCount", false],
      ["Occurrence Number", "occurrenceNumber", false],
      ["Archive Document ID", "archiveDocumentId", true],
      ["Original Source Document ID", "originalSourceId", true],
      ["Reason Row Appears", "reason", false],
    ];
  }

  return [
    ...identity,
    ["Warehouse Location", "location", true],
    ["Reconciliation Status", "status", false],
    ["Warehouse Occurrence Count", "warehouseOccurrenceCount", false],
    ["NAV Occurrence Count", "navOccurrenceCount", false],
    ["Warehouse Document ID", "warehouseDocumentId", true],
    ["NAV Document IDs", "navDocumentIds", true],
    ["Reason Row Appears", "reason", false],
  ];
}

function exportCellValue(row, key, isDate) {
  const value = row[key];
  if (isDate) return formatDateTime(value);
  if (Array.isArray(value)) return value.join(", ");
  return value == null ? "" : value;
}

function markIdentifierColumnsAsText(worksheet, columns, rowCount) {
  columns.forEach((column, columnIndex) => {
    if (!column[2]) return;
    for (let rowIndex = 1; rowIndex <= rowCount; rowIndex += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = worksheet[address];
      if (!cell) continue;
      cell.t = "s";
      cell.v = asString(cell.v);
      cell.z = "@";
    }
  });
}

export function buildMovementWorkbook({
  mode,
  rows,
  filters,
  warnings,
  generatedAt = new Date(),
}) {
  const modeDetails = MODE_EXPORT_DETAILS[mode] || {
    label: mode,
    filename: "cylinder-movement-report",
  };
  const columns = exportColumns(mode);
  const resultRows = rows || [];
  const resultSheetData = [
    columns.map((column) => column[0]),
    ...resultRows.map((row) =>
      columns.map((column) => exportCellValue(row, column[1], column[3]))
    ),
  ];
  const resultSheet = XLSX.utils.aoa_to_sheet(resultSheetData);
  markIdentifierColumnsAsText(resultSheet, columns, resultRows.length);
  resultSheet["!autofilter"] = {
    ref: resultSheet["!ref"] || "A1:A1",
  };

  const effectiveFilters = { ...(filters || {}) };
  if (
    mode === REPORT_MODES.WAREHOUSE &&
    !effectiveFilters.classification
  ) {
    effectiveFilters.classification =
      "Default removals (stocktake backups excluded)";
  } else if (effectiveFilters.classification === "__all__") {
    effectiveFilters.classification = "All classifications";
  }

  const activeFilters = Object.entries(effectiveFilters).filter(
    ([, value]) => value !== "" && value != null
  );
  const metadataRows = [
    ["Report mode", modeDetails.label],
    ["Generated at", generatedAt.toISOString()],
    ["Result count", resultRows.length],
    [],
    ["Active filters"],
    ...(activeFilters.length
      ? activeFilters.map(([key, value]) => [key, asString(value)])
      : [["None", ""]]),
    [],
    ["Warnings / completeness notes"],
    ...((warnings || []).length
      ? warnings.map((warning) => [warning])
      : [["None"]]),
  ];
  const metadataSheet = XLSX.utils.aoa_to_sheet(metadataRows);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, resultSheet, "Results");
  XLSX.utils.book_append_sheet(workbook, metadataSheet, "Report metadata");

  const dateStamp = generatedAt.toISOString().slice(0, 10);
  return {
    workbook,
    filename: `${modeDetails.filename}-${dateStamp}.xlsx`,
  };
}

export function downloadMovementWorkbook(options) {
  const { workbook, filename } = buildMovementWorkbook(options);
  XLSX.writeFile(workbook, filename);
}
